import clsx from 'clsx'
import {FC, useMemo, useState} from 'react'
import {
  generatePassword,
  passwordAccepted,
  passwordChecks,
  passwordStrength,
  STRENGTH_LABELS,
  usernameProblem,
} from '../../profile/core/credentialRules'

export type CredentialValues = {username: string; password: string; repeat: string}

/** What stops the username and password from being saved, as the server checks them (it decides). */
export const credentialProblems = (
  values: CredentialValues,
  currentUsername?: string | null,
  mobile?: string | null
) => ({
  username: usernameProblem(values.username, currentUsername),
  password: !values.password
    ? 'رمز عبور را وارد کنید.'
    : passwordAccepted(values.password, values.username, mobile)
    ? ''
    : 'رمز عبور همه‌ی شرایط زیر را ندارد.',
  repeat: values.repeat !== values.password ? 'تکرار رمز با رمز عبور یکی نیست.' : '',
})

type Props = {
  idPrefix: string
  values: CredentialValues
  onChange: (values: CredentialValues) => void
  /** A username the account already has: kept as it is, whatever its shape. */
  currentUsername?: string | null
  mobile?: string | null
  /** Show the problems (after a submit or once a field was left). */
  showErrors: boolean
  /** The server's word on the username (taken). */
  usernameServerError?: string
  disabled?: boolean
}

/**
 * «نام کاربری و رمز عبور» on the pages before the panel: the username, a password with its rules as a checklist and
 * a strong one on request, and the password again. The same rules as «امنیت و ورود» (profile/core/credentialRules).
 */
export const CredentialFields: FC<Props> = ({
  idPrefix,
  values,
  onChange,
  currentUsername,
  mobile,
  showErrors,
  usernameServerError,
  disabled,
}) => {
  const [visible, setVisible] = useState(false)
  const problems = credentialProblems(values, currentUsername, mobile)
  const checks = useMemo(
    () => passwordChecks(values.password, values.username, mobile),
    [values.password, values.username, mobile]
  )
  const strength = passwordStrength(values.password)
  const usernameError = usernameServerError || (showErrors ? problems.username : '')
  const passwordError = showErrors ? problems.password : ''
  const repeatError = showErrors || values.repeat ? problems.repeat : ''

  const set = (patch: Partial<CredentialValues>) => onChange({...values, ...patch})

  const suggest = () => {
    const generated = generatePassword()
    set({password: generated, repeat: generated})
    setVisible(true)
  }

  return (
    <>
      <div className='auth-field'>
        <label htmlFor={`${idPrefix}-username`}>نام کاربری</label>
        <div className='auth-input-wrap'>
          <i className='las la-at' aria-hidden='true' />
          <input
            id={`${idPrefix}-username`}
            className={clsx('form-control englishText', usernameError && 'is-invalid')}
            dir='ltr'
            value={values.username}
            maxLength={25}
            spellCheck={false}
            autoComplete='username'
            disabled={disabled}
            aria-invalid={Boolean(usernameError)}
            aria-describedby={`${idPrefix}-username-hint`}
            onChange={(event) => set({username: event.target.value.replace(/\s/g, '')})}
          />
        </div>
        <small
          id={`${idPrefix}-username-hint`}
          className={clsx(usernameError && 'auth-field__error')}
        >
          {usernameError || 'حروف انگلیسی، رقم و نقطه؛ ۵ تا ۲۵ نویسه.'}
        </small>
      </div>

      <div className='account-entry__grid'>
        <div className='auth-field'>
          <label htmlFor={`${idPrefix}-password`}>رمز عبور</label>
          <div className='auth-input-wrap auth-input-wrap--password'>
            <i className='las la-lock' aria-hidden='true' />
            <input
              id={`${idPrefix}-password`}
              className={clsx('form-control englishText', passwordError && 'is-invalid')}
              type={visible ? 'text' : 'password'}
              dir='ltr'
              value={values.password}
              maxLength={64}
              autoComplete='new-password'
              disabled={disabled}
              aria-invalid={Boolean(passwordError)}
              aria-describedby={`${idPrefix}-password-rules`}
              onChange={(event) => set({password: event.target.value})}
            />
            <button
              type='button'
              className='auth-password-toggle'
              onClick={() => setVisible((value) => !value)}
              aria-label={visible ? 'پنهان کردن رمز' : 'نمایش رمز'}
              title={visible ? 'پنهان کردن رمز' : 'نمایش رمز'}
            >
              <i className={clsx('las', visible ? 'la-eye-slash' : 'la-eye')} aria-hidden='true' />
            </button>
          </div>
          {passwordError && <small className='auth-field__error'>{passwordError}</small>}
        </div>

        <div className='auth-field'>
          <label htmlFor={`${idPrefix}-repeat`}>تکرار رمز عبور</label>
          <div className='auth-input-wrap'>
            <i className='las la-redo-alt' aria-hidden='true' />
            <input
              id={`${idPrefix}-repeat`}
              className={clsx('form-control englishText', repeatError && 'is-invalid')}
              type={visible ? 'text' : 'password'}
              dir='ltr'
              value={values.repeat}
              maxLength={64}
              autoComplete='new-password'
              disabled={disabled || !values.password}
              aria-invalid={Boolean(repeatError)}
              aria-describedby={repeatError ? `${idPrefix}-repeat-error` : undefined}
              onChange={(event) => set({repeat: event.target.value})}
            />
          </div>
          {repeatError && (
            <small id={`${idPrefix}-repeat-error`} className='auth-field__error'>
              {repeatError}
            </small>
          )}
        </div>
      </div>

      <ul
        id={`${idPrefix}-password-rules`}
        className='account-entry__rules'
        aria-label='شرایط رمز عبور'
      >
        {checks.map((check) => (
          <li key={check.key} className={clsx(check.passed && 'is-passed')}>
            <i
              className={clsx('las', check.passed ? 'la-check-circle' : 'la-circle')}
              aria-hidden='true'
            />
            {check.label}
            <span className='visually-hidden'>
              {check.passed ? ' (رعایت شده)' : ' (رعایت نشده)'}
            </span>
          </li>
        ))}
        {values.password && (
          <li aria-live='polite'>
            <i className='las la-signal' aria-hidden='true' />
            قدرت رمز: {STRENGTH_LABELS[strength]}
          </li>
        )}
      </ul>

      <button
        type='button'
        className='auth-link-button auth-link-button--accent mb-4'
        onClick={suggest}
        disabled={disabled}
      >
        <i className='las la-magic' aria-hidden='true' /> ساخت رمز قوی
      </button>
    </>
  )
}
